import React, { FC, MouseEvent, useState } from 'react';
import { Button, Menu, MenuItem } from '@mui/material';

import { AccountCircle, ChevronLeft, EditLocation } from '@mui/icons-material';
import MenuIcon from '@mui/icons-material/Menu';

import { LinkClearStyles } from 'elements/link';
import { AlertDialog } from 'components/Alert';
import {
  AuthContainer,
  AuthDisplayState,
} from 'components/authorizationPopups';
import { useAuthContext } from 'components/authorization/AuthProvider';
import { sendEmailVerificationCode } from 'components/authorization/cognitoUtils';

import {
  AppBarStyled,
  HeaderWrapper,
  NavLeft,
  NavRight,
  Profile,
  ProfileEmail,
  SectionDesktop,
  Toolbar,
  LeftNavIcon,
} from './styles';

interface MenuItem {
  title: string;
  link?: string;
  action?: () => void;
}

interface props {
  toggleDrawer: () => void;
  drawerOpen: boolean;
  setAuthModalDisplay: (value: AuthDisplayState) => void;
  authModalDisplay: AuthDisplayState;
}

export const Header: FC<props> = ({
  toggleDrawer,
  drawerOpen,
  setAuthModalDisplay,
  authModalDisplay,
}) => {
  const { authUser, logout } = useAuthContext();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedMenu, setSelectedMenu] = useState<Array<MenuItem>>([]);

  const [showEmailVerification, setShowEmailVerification] = useState(false);

  const isMenuOpen = Boolean(anchorEl);

  const handleDrawerOpenToggle = () => {
    toggleDrawer();
  };

  const handleMenuOpen = (event: MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleProfileMenuOpen = (event: MouseEvent<HTMLElement>): void => {
    setSelectedMenu(profileMenu);
    handleMenuOpen(event);
  };

  const handleLocationMenuOpen = (event: MouseEvent<HTMLElement>): void => {
    setSelectedMenu(locationMenu);
    handleMenuOpen(event);
  };
  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleLogout = () => {
    handleMenuClose();
    logout(true);
  };

  const handleLogin = () => {
    setAuthModalDisplay(AuthDisplayState.SignIn);
  };

  const emailVerificationReloadClick = () => {
    handleMenuClose();
    location.reload();
  };

  const handleVerifyEmail = () => {
    sendEmailVerificationCode()
      .then(() => setShowEmailVerification(true))
      .catch(() => console.log('Send email verificatin code in header failed'));
  };

  const handleResetPassword = () => {
    setAuthModalDisplay(AuthDisplayState.ResetPassword);
    handleMenuClose();
  };

  const locationMenu: MenuItem[] = authUser?.locations
    ?.sort((locA, locB) =>
      locA.name.toLowerCase().localeCompare(locB.name.toLowerCase()),
    )
    ?.map(
      ({
        name,
        id,
        timezone,
        parentLocation,
        usesParentLocationPlans,
        providerDirectProductPurchase,
        providerDirectPurchaseProfitMargin,
        bookingAllowed,
      }) => ({
        title: name,
        action: () => {
          authUser?.setSelectedLocation({
            id,
            name,
            timezone,
            parentLocation,
            usesParentLocationPlans,
            providerDirectProductPurchase,
            providerDirectPurchaseProfitMargin,
            bookingAllowed,
          });
          handleMenuClose();
        },
      }),
    );

  const profileMenu: MenuItem[] = [
    {
      title: 'Logout',
      action: handleLogout,
    },
  ];

  // A signed in user may still not have verified their email, though an email verification code will
  // have been send to them, so let them put in the code now.
  if (!authUser.email_verified) {
    profileMenu.unshift({ title: 'Verify Email', action: handleVerifyEmail });
  } else {
    // If user has already verified email then allow them to reset their password while they are logged in.
    profileMenu.unshift({
      title: 'Reset Password',
      action: handleResetPassword,
    });
  }

  const renderMenu = (menu: Array<MenuItem>) => {
    return (
      <Menu
        anchorEl={anchorEl}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
        keepMounted
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        open={isMenuOpen}
        onClose={handleMenuClose}
      >
        {menu.map((item) =>
          item.link ? (
            <MenuItem key={item.title}>
              <LinkClearStyles to={item.link}>{item.title}</LinkClearStyles>
            </MenuItem>
          ) : (
            <MenuItem onClick={item.action} key={item.title}>
              {item.title}
            </MenuItem>
          ),
        )}
      </Menu>
    );
  };

  const displayName = authUser?.firstName ?? authUser.email ?? '';

  return (
    <React.Fragment>
      <HeaderWrapper>
        <AppBarStyled position="fixed">
          <Toolbar>
            <NavLeft>
              {/*<Logo />*/}
              <LeftNavIcon
                aria-label="open drawer"
                onClick={handleDrawerOpenToggle}
              >
                {drawerOpen ? <ChevronLeft /> : <MenuIcon />}
              </LeftNavIcon>
            </NavLeft>
            {authUser?.authorized ? (
              <NavRight>
                {authUser?.locations?.length > 0 && (
                  <Button color="inherit" onClick={handleLocationMenuOpen}>
                    <ProfileEmail>
                      {authUser.selectedLocation?.name ?? 'Select Location'}
                    </ProfileEmail>
                    <EditLocation color="inherit" />
                  </Button>
                )}
                <SectionDesktop>
                  <Profile tabIndex={0} onClick={handleProfileMenuOpen}>
                    <Button color="inherit">
                      <ProfileEmail>{displayName}</ProfileEmail>
                      <AccountCircle color="inherit" />
                    </Button>
                  </Profile>
                </SectionDesktop>
              </NavRight>
            ) : (
              <NavRight>
                {!authUser?.authorized && (
                  <Button
                    color="inherit"
                    aria-label="login"
                    name="login"
                    id="header-login"
                    onClick={handleLogin}
                  >
                    Login
                  </Button>
                )}
                {authUser?.authorized && (
                  <Button
                    color="inherit"
                    aria-label="logout"
                    name="logout"
                    id="header-logout"
                    onClick={handleLogout}
                  >
                    Logout
                  </Button>
                )}
              </NavRight>
            )}
          </Toolbar>
        </AppBarStyled>
        {renderMenu(selectedMenu)}
      </HeaderWrapper>
      <AuthContainer
        show={authModalDisplay}
        displayStateEvent={setAuthModalDisplay}
      ></AuthContainer>
      <AlertDialog
        title="Email Code Sent"
        content="Click the verification link that we just emailed to you.  After clicking, you can continue from the new tab, or refresh this page. "
        option0Text="Refresh (after clicking the email link)"
        sendUserClick={emailVerificationReloadClick}
        open={showEmailVerification}
      />
    </React.Fragment>
  );
};
